import { simpleStringify, useQuery } from 'components/RouterQuery';
import { FilterValue } from '../components/TodoFilter';
import { useNavigate } from 'react-router-dom';
import { useMount, useUnmount } from 'ahooks';
import { useMemo, useRef } from 'react';
import dayjs from 'lib/dayjs';

export const defaultPage = {
  pageNum: 1,
  pageSize: 10,
};

function queryStringToNumber<T extends number>(
  queryString: string | undefined,
  fallback: T,
  validator: (num: number) => num is T = (num): num is T => !isNaN(num),
): T {
  if (!queryString) {
    return fallback;
  }

  const num = parseInt(queryString, 10);

  return validator(num) ? num : fallback;
}

let filterCache: FilterValue;

type QueryFilterValue = Util.Query<FilterValue>;

export function useFilterQuery() {
  const query = useQuery<QueryFilterValue>();

  const nav = useNavigate();

  const mountedRef = useRef(false);

  const queryFilterValue = useMemo(() => {
    if (filterCache && !Object.keys(query).length) {
      return filterCache;
    }

    const {
      type,
      todoStatus,
      timeBegin = getDefaultTimeBegin(),
      timeEnd = getDefaultTimeEnd(),
      pageNum: queryPageNum,
      pageSize: queryPageSize,
      filterErpReview = false,
      ...others
    } = query;

    const queryFilterValue: FilterValue = {
      ...others,
      timeBegin,
      timeEnd,
      filterErpReview,
      type: type ? queryStringToNumber(type as unknown as string, -1) : undefined,
      todoStatus: queryStringToNumber(todoStatus, -1),
      pageNum: queryStringToNumber(queryPageNum, defaultPage.pageNum),
      pageSize: queryStringToNumber(queryPageSize, defaultPage.pageSize),
    };

    return queryFilterValue;
  }, [query]);

  useUnmount(() => {
    filterCache = queryFilterValue;
  });

  useMount(() => {
    mountedRef.current = true;
  });

  const onQueryFilterChange = (filter: FilterValue) => nav(`?${simpleStringify(filter)}`);

  return {
    query,
    queryFilterValue,
    onQueryFilterChange,
  };
}

export const getDefaultTimeBegin = () =>
  dayjs().subtract(29, 'days').startOf('days').valueOf().toString();
export const getDefaultTimeEnd = () => dayjs().endOf('days').valueOf().toString();
